import type { Task } from '@/types';
import type { TaskContext } from '@/lib/aiClient';

const HORIZON_DAYS = 3;
const MAX_TASKS = 30;
const DAY_MS = 86_400_000;

/**
 * Bounded snapshot of the tasks relevant to an AI command: today's scheduled
 * items, the unscheduled inbox, and anything scheduled within the next ~3 days.
 * Excludes done/skipped. Scheduled-by-time first, then unscheduled. Pure.
 */
export function selectAiTaskContext(tasks: Task[], nowMs: number): TaskContext[] {
  const now = new Date(nowMs);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const horizonEnd = startOfToday + (HORIZON_DAYS + 1) * DAY_MS;

  const relevant = tasks.filter((t) => {
    if (t.status === 'done' || t.status === 'skipped') return false;
    if (t.status === 'inbox') return true;
    const s = t.scheduledStart?.getTime();
    if (s == null) return true;
    return s >= startOfToday && s < horizonEnd;
  });

  relevant.sort((a, b) => {
    const sa = a.scheduledStart?.getTime() ?? Number.POSITIVE_INFINITY;
    const sb = b.scheduledStart?.getTime() ?? Number.POSITIVE_INFINITY;
    return sa - sb;
  });

  return relevant.slice(0, MAX_TASKS).map((t) => ({
    id: t.id,
    title: t.title,
    status: t.status,
    priority: t.priority,
    durationMinutes: t.durationMinutes,
    ...(t.scheduledStart ? { scheduledStartMs: t.scheduledStart.getTime() } : {}),
  }));
}
